import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Radio, Tooltip, IconButton } from '@mui/material';
import { getDynamicFieldControlType } from 'utils/functions';
import { StyledTableRow, StyledTableBodyCell } from 'components/common/Styles';
import { Delete, Edit, Save, Close } from '@mui/icons-material';
import { DynamicField, TextField } from '@totalsoft/rocket-ui';
import { get, set } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { pvtRange, pvtList } from './constants';
import { emptyString } from 'utils/constants';
import { valueOrDefault } from 'utils/functions';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_ITEMS_WRITE } from 'constants/rightsCodes';

const PropertyValueItem = ({
  value,
  index,
  propertyValueTypeCode,
  propertyTypeCode,
  onSaveValue,
  onCancelEditValue,
  onDelete,
  defaultValue,
  onSelectionChanged,
  isDirty
}) => {
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_ITEMS_WRITE);
  const [disabled, setDisabled] = useState(true);

  const handleEditValue = useCallback(() => {
    (value.editValue |> set)(value.value |> get);
    (value.inEdit |> set)(true);
    setDisabled(true);
  }, [value]);

  const onChangeAdapter = useCallback(
    onChange => value => {
      onChange(value);
      setDisabled(false);
    },
    []
  );

  return (
    <StyledTableRow hover key={value.propertyValueId |> get}>
      {propertyValueTypeCode === pvtList && (
        <StyledTableBodyCell style={{ textAlign: 'start', width: '15%' }}>
          <Radio
            checked={defaultValue ? (value.value |> get) === defaultValue : false}
            onChange={() => onSelectionChanged(value.value |> get)}
            value={value.value |> get}
            disabled={!(value.propertyValueId |> get) || !canWrite}
          />
        </StyledTableBodyCell>
      )}
      <StyledTableBodyCell style={{ textAlign: 'start', width: '35%' }}>
        {value.inEdit |> get ? (
          <>
            <DynamicField
              id={`value_${index}`}
              controlType={getDynamicFieldControlType(propertyTypeCode)}
              label={t('Items.Properties.Value')}
              value={value.editValue |> get || valueOrDefault(emptyString)}
              onChange={onChangeAdapter(set(value.editValue))}
            />
          </>
        ) : (
          <>{(value.value |> get) instanceof Date ? (value.value |> get).toLocaleDateString() : (value.value |> get).toString()}</>
        )}
      </StyledTableBodyCell>
      {propertyValueTypeCode === pvtRange && (
        <>
          {value.inEdit |> get ? (
            <>
              <StyledTableBodyCell>
                <TextField
                  disabled={!canWrite}
                  id={`value_${value.propertyMinValue |> get}`}
                  fullWidth
                  onChange={onChangeAdapter(set(value.propertyMinValue))}
                  isNumeric
                  value={value.propertyMinValue |> get || valueOrDefault(emptyString)}
                  label={t('Items.Values.MinValue')}
                />
              </StyledTableBodyCell>
              <StyledTableBodyCell>
                <TextField
                  disabled={!canWrite}
                  id={`value_${value.propertyMaxValue |> get}`}
                  fullWidth
                  onChange={onChangeAdapter(set(value.propertyMaxValue))}
                  isNumeric
                  value={value.propertyMaxValue |> get || valueOrDefault(emptyString)}
                  label={t('Items.Values.MaxValue')}
                />
              </StyledTableBodyCell>
            </>
          ) : (
            <>
              <StyledTableBodyCell>{value.propertyMinValue |> get}</StyledTableBodyCell>
              <StyledTableBodyCell>{value.propertyMaxValue |> get}</StyledTableBodyCell>
            </>
          )}
        </>
      )}
      <StyledTableBodyCell>
        <Box display='flex' gap={1} alignItems='center' justifyContent='end'>
          {value.inEdit |> get ? (
            <>
              <Tooltip title={t('Close')}>
                <IconButton
                  size='small'
                  color='primary'
                  onClick={() => {
                    (value.inEdit |> set)(false);
                    (value.editValue |> set)(value.value |> get);
                    (value.propertyValueId |> get) === null && onCancelEditValue();
                  }}
                >
                  <Close fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('Items.Save')}>
                <IconButton
                  size='small'
                  color='primary'
                  onClick={() => {
                    return onSaveValue(value |> get, () => {
                      (value.inEdit |> set)(false);
                      (value.value |> set)(value.editValue |> get);
                    });
                  }}
                  disabled={!value.inEdit || disabled || !canWrite || !isDirty}
                >
                  <Save fontSize='small' />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title={t('Items.Edit')}>
              <IconButton disabled={!canWrite} size='small' color='primary' onClick={handleEditValue}>
                <Edit fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('Items.Delete')}>
            <IconButton
              disabled={!(value.propertyValueId |> get) || !canWrite}
              size='small'
              color='primary'
              onClick={() => onDelete(value.propertyValueId |> get)}
            >
              <Delete fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      </StyledTableBodyCell>
    </StyledTableRow>
  );
};

PropertyValueItem.propTypes = {
  value: PropTypes.object.isRequired,
  valueLensDirtyInfo: PropTypes.object,
  index: PropTypes.number,
  propertyValueTypeCode: PropTypes.string,
  propertyTypeCode: PropTypes.string,
  onSaveValue: PropTypes.func,
  onCancelEditValue: PropTypes.func,
  onDelete: PropTypes.func,
  defaultValue: PropTypes.string,
  onSelectionChanged: PropTypes.func,
  isDirty: PropTypes.bool
};

export default PropertyValueItem;
